import { useEffect, useState } from "react";
import api, { formatApiError, PREMIUM_EVENT } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, Check, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { DiscountCodeField } from "@/components/DiscountCodeField";

const PERKS = [
  "Dieta settimanale su misura creata da DietFlix",
  "Lista alimenti personalizzata",
  "Sostituzioni dinamiche dei pasti",
  "Consigli delle analisi applicati alla dieta",
];

export default function UpgradeDialog() {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [discount, setDiscount] = useState(null);

  useEffect(() => {
    const handler = async () => {
      setOpen(true);
      if (plans.length === 0) {
        try {
          const { data } = await api.get("/subscription");
          if (!data.is_premium) setPlans(data.plans || []);
          else setOpen(false); // already premium, nothing to sell
        } catch {}
      }
    };
    window.addEventListener(PREMIUM_EVENT, handler);
    return () => window.removeEventListener(PREMIUM_EVENT, handler);
  }, [plans.length]);

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin, discount_code: discount?.code });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="upgrade-dialog">
        <div className="bg-gradient-to-br from-primary to-accent px-6 pt-7 pb-6 text-primary-foreground">
          <div className="h-12 w-12 rounded-2xl bg-white/20 grid place-items-center mb-3">
            <Crown className="h-6 w-6" />
          </div>
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="text-2xl font-heading text-primary-foreground">Sblocca DietFlix Premium</DialogTitle>
            <DialogDescription className="text-primary-foreground/90">
              Questa funzione fa parte di Premium. Attivalo ora e sblocca subito dieta, alimenti e consigli di DietFlix su misura.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-4">
          <ul className="space-y-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {p}</li>
            ))}
          </ul>

          {plans.length > 0 && <DiscountCodeField discount={discount} onChange={setDiscount} />}
          <div className="space-y-2.5">
            {plans.map((plan) => {
              const yearly = plan.lookup_key === "dietflix_yearly";
              return (
                <Button
                  key={plan.lookup_key}
                  className="w-full h-12 justify-between"
                  variant={yearly ? "default" : "outline"}
                  disabled={!!busy}
                  onClick={() => subscribe(plan.lookup_key)}
                  data-testid={`upgrade-subscribe-${plan.lookup_key}`}
                >
                  <span className="flex items-center gap-2">
                    {yearly ? <Zap className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                    {busy === plan.lookup_key ? "Reindirizzamento…" : `Passa a Premium · ${plan.label}`}
                  </span>
                  <span className="font-heading font-semibold">
                    {discount && <span className="mr-1 text-xs font-normal line-through opacity-70">€{plan.price.toFixed(2).replace(".", ",")}</span>}
                    €{(discount ? discount.prices[plan.lookup_key] : plan.price).toFixed(2).replace(".", ",")}/{plan.interval}
                    {yearly && <span className="ml-1 text-[10px] font-normal opacity-80">-25%</span>}
                  </span>
                </Button>
              );
            })}
            {plans.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">Caricamento dei piani…</p>
            )}
          </div>

          <button
            className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setOpen(false)}
            data-testid="upgrade-later"
          >
            Magari più tardi
          </button>
          <p className="text-[11px] text-muted-foreground text-center">Pagamento sicuro con Stripe · disdici quando vuoi</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
